import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert } from '@osd/ui/alert';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Field } from '@osd/ui/field';
import { Spinner } from '@osd/ui/spinner';
import { Heading } from '@osd/ui/heading';
import { Input } from '@osd/ui/input';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import type { InvitePublic } from '@osd/shared-types';
import { api } from '../../lib/api';

/**
 * /admin/invites — admin-only.
 *
 * Members-only registration: the admin creates invites here, the
 * system returns the plaintext token ONCE (the DB stores only its
 * SHA-256 hash). The admin pastes the token into an email/Slack.
 *
 * Same auth gate as /admin/approvals: requires the isAdmin flag in
 * localStorage, which AuthProvider stashes on /me success.
 *
 * (root/osd#27, root/osd#22 Phase 2)
 */
export const Route = createFileRoute('/admin/invites')({
  component: InvitesPage,
  beforeLoad: () => {
    if (typeof window === 'undefined') return;
    const isAdmin = localStorage.getItem('osd.auth.isAdmin') === 'true';
    if (!isAdmin) {
      throw redirect({ to: '/' });
    }
  },
});

interface CreatedInvite {
  id: string;
  token: string;
  expiresAt: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 16).replace('T', ' ');
}

interface InviteStatus {
  readonly label: 'used' | 'expired' | 'active';
  readonly tone: 'ok' | 'muted' | 'warn';
}

function statusFor(inv: InvitePublic): InviteStatus {
  if (inv.usedAt !== null) return { label: 'used', tone: 'muted' };
  if (new Date(inv.expiresAt) <= new Date()) return { label: 'expired', tone: 'warn' };
  return { label: 'active', tone: 'ok' };
}

function statusColor(tone: InviteStatus['tone']): string {
  if (tone === 'warn') return 'var(--osd-color-warning, #b45309)';
  if (tone === 'ok') return 'var(--osd-color-success, #15803d)';
  return 'var(--osd-color-muted, #71717a)';
}

function InvitesPage() {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [ttlDays, setTtlDays] = useState(14);
  // The freshly-created invite — the plaintext token is surfaced
  // ONCE here, with a copy-to-clipboard button. After the admin
  // dismisses, the token is gone forever.
  const [created, setCreated] = useState<CreatedInvite | null>(null);

  const invites = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api.admin().listInvites(),
    refetchInterval: 10_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.admin().createInvite({
        email: email.trim() === '' ? undefined : email.trim(),
        expiresInDays: ttlDays,
      }),
    onSuccess: (res) => {
      setCreated({ id: res.id, token: res.token, expiresAt: res.expiresAt });
      setFeedback(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
      setEmail('');
      setTtlDays(14);
    },
    onError: (err) => setFeedback(`Create invite failed: ${(err as Error).message}`),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.admin().revokeInvite(id),
    onSuccess: () => {
      setFeedback('Invite revoked.');
      void qc.invalidateQueries({ queryKey: ['admin', 'invites'] });
    },
    onError: (err) => setFeedback(`Revoke failed: ${(err as Error).message}`),
  });

  async function copyTokenToClipboard(token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(token);
      setFeedback('Token copied to clipboard.');
    } catch {
      setFeedback('Could not copy automatically — please select the token manually.');
    }
  }

  return (
    <Stack gap={4}>
      <Card>
        <Stack gap={4}>
          <header>
            <Heading level={1}>Invites</Heading>
            <Text muted>
              Create an invite to allow a new member to register. The token appears once — copy it
              before dismissing.
            </Text>
          </header>

          {feedback !== null ? <Alert variant="danger">{feedback}</Alert> : null}

          <Stack gap={3}>
            <Field label="Email (optional)">
              <Input
                id="invite-email"
                type="email"
                value={email}
                placeholder="leave blank for any email"
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={create.isPending}
              />
            </Field>
            <Field label="Expires in (days)">
              <Input
                id="invite-ttl"
                type="number"
                min={1}
                max={90}
                value={ttlDays}
                onChange={(e) => setTtlDays(Number(e.currentTarget.value) || 1)}
                disabled={create.isPending}
              />
            </Field>
            <Button
              type="button"
              variant="primary"
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create invite'}
            </Button>
          </Stack>

          {created !== null ? (
            <Alert variant="success">
              <Stack gap={2}>
                <Text>
                  <strong>New invite created.</strong> Copy this token now — you will not see it
                  again.
                </Text>
                <code
                  style={{
                    display: 'block',
                    padding: '0.5rem 0.75rem',
                    background: 'var(--osd-color-surface-2)',
                    borderRadius: 'var(--osd-radius-md, 6px)',
                    wordBreak: 'break-all',
                  }}
                >
                  {created.token}
                </code>
                <Stack direction="row" gap={2}>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void copyTokenToClipboard(created.token)}
                  >
                    Copy token
                  </Button>
                  <Button type="button" variant="default" onClick={() => setCreated(null)}>
                    Dismiss
                  </Button>
                </Stack>
                <Text muted>Expires {formatDate(created.expiresAt)} UTC.</Text>
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <Heading level={2}>Existing invites</Heading>
          {invites.isPending ? <Spinner size="sm" ariaLabel="Loading invites" /> : null}
          {invites.isError ? (
            <Alert variant="danger">
              Could not load invites: {(invites.error as Error).message}
            </Alert>
          ) : null}
          {invites.data?.invites.length === 0 ? <Text muted>No invites yet.</Text> : null}
          {invites.data && invites.data.invites.length > 0 ? (
            <Table columns="1.5fr 7rem 1.5fr 7rem" caption="Existing invites">
              <Table.Header>
                <Table.Row>
                  <Table.Head>Email</Table.Head>
                  <Table.Head>Status</Table.Head>
                  <Table.Head>Timeline</Table.Head>
                  <Table.Head>Action</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {invites.data.invites.map((inv) => {
                  const status = statusFor(inv);
                  const isUsed = inv.usedAt !== null;
                  return (
                    <Table.Row key={inv.id}>
                      <Table.Cell>
                        <strong>{inv.email ?? 'any email'}</strong>
                      </Table.Cell>
                      <Table.Cell>
                        <span style={{ color: statusColor(status.tone), fontSize: '0.85em' }}>
                          [{status.label}]
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Text muted style={{ fontSize: '0.85em' }}>
                          created {formatDate(inv.createdAt)}
                          {' · '}
                          {isUsed
                            ? `used ${formatDate(inv.usedAt)}`
                            : `expires ${formatDate(inv.expiresAt)}`}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          type="button"
                          variant="default"
                          disabled={revoke.isPending || isUsed}
                          onClick={() => revoke.mutate(inv.id)}
                        >
                          {isUsed ? 'used' : 'Revoke'}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
